import React, { useEffect, useMemo, useState } from "react";
import isEmpty from "lodash/isEmpty";
import {
    CloseOutlined,
    AlertCircleOutlined,
    XOutlined,
} from "@phonepe/enigma-ui-icons";
import dayjs from "dayjs";
import { useEnigmaToken } from "@hooks/useEnigmaToken";
import { EnigmaTokens } from "@type/tokens";
import { Button } from "@components/button";
import { Tooltip } from "@components/tooltip";
import { Loader } from "@components/loader";
import { HelperText } from "@components/helperText";
import { TypographyTypes } from "@components/typography";
import {
    filterModalFooterClass,
    filterModalBodyClass,
    modalCloseButtonClass,
    StyledFilterModal,
    StyledModalFooter,
    StyledModalTitle,
    StyledFilterMenuWrapper,
    StyledFilterMenuItem,
    FilterCountPlaceHolder,
    StyledFilterCount,
    StyledNoResultWrapper,
    StyledCustomFilterRenderer,
    StyledLoaderWrapper,
    removeClass,
    FilterRendererWrapper,
    errorIcon,
    errorTitle,
    getFooterClassName,
} from "./filterModal.styles";
import {
    IFilterModalProps,
    IFilterOption,
    TErrorData,
    TErrorMessages,
    TFilterTypeConfig,
} from "./filterModal.types";
import {
    FILTER_MESSAGES,
    FILTER_MODAL_DATA_IDS,
    FILTER_TYPES,
    MAX_ALLOWED_DATE_RANGE,
} from "./filterModal.constants";
import CheckboxFilters from "./components/CheckboxFilters";
import RadioFilters from "./components/RadioFilters";
import ListFilters from "./components/ListFilters";
import DateRangeFilter from "./components/DateRangeFilter";

export const FilterModal = <T extends Record<keyof T, IFilterOption[]>>(
    props: IFilterModalProps<T>
) => {
    const {
        isOpen,
        isLoading,
        title = "Filters",
        mode,
        selectedFilters = {},
        defaultFilterType,
        filterTypeConfig,
        onClose,
        onApply,
        onClearAll,
        onFilterTypeChange,
        primaryCtaText = "Apply",
        secondaryCtaText = "Clear All Filters",
    } = props;
    const tokens: EnigmaTokens = useEnigmaToken();

    const filterItemsArr: Array<TFilterTypeConfig<T>> = useMemo(
        () => Object.values(filterTypeConfig),
        [filterTypeConfig]
    );

    const [errorMessage, setErrorMessage] = useState<
        Partial<TErrorMessages<T>>
    >({});
    const [localSelectedFilters, setLocalSelectedFilters] =
        useState<Partial<T>>(selectedFilters);
    const [selectedFilterType, setSelectedFilterType] = useState<keyof T>(
        filterItemsArr[0]?.key
    );

    const generateErrorMessages = () => {
        const errors = filterItemsArr.reduce(
            (acc, item) => {
                if (
                    item?.isRequired &&
                    isEmpty(localSelectedFilters[item.key])
                ) {
                    acc[item.key] = `${item.title} is Required *`;
                } else if (item.key === "dateRange") {
                    acc[item.key] = errorMessage[item.key];
                } else {
                    acc[item.key] = "";
                }
                return acc;
            },
            { ...errorMessage }
        );
        setErrorMessage(errors);
    };
    useEffect(() => {
        generateErrorMessages();
    }, [filterItemsArr, localSelectedFilters]);

    // private functions start -----
    const _handleClearAllClick = () => {
        onClearAll?.();
        setLocalSelectedFilters({});
        setSelectedFilterType(filterItemsArr[0]?.key);
    };

    const handleError = (errorData: TErrorData<T>) => {
        const { iserror, key, err } = errorData;
        setErrorMessage((prev) => ({
            ...prev,
            [key]: iserror ? err : undefined,
        }));
        const selectedFilterConfig = filterTypeConfig[selectedFilterType];
        if (selectedFilterConfig?.onError && err) {
            selectedFilterConfig.onError(err);
        }
    };
    const _handleApplyClick = () => {
        onApply(localSelectedFilters);
    };

    const _handleCloseClick = () => {
        setLocalSelectedFilters(selectedFilters);
        onClose();
    };

    const _handleFilterTypeClick = (filterType: keyof T) => {
        const isFilterTypeDisabled =
            filterTypeConfig[filterType]?.isDisabled?.(
                localSelectedFilters
            )?.value;

        if (isFilterTypeDisabled || filterType === selectedFilterType) return;

        setSelectedFilterType(filterType);
        onFilterTypeChange(filterType, localSelectedFilters);
    };
    const _handleClearFilter = (filterType: keyof T) => {
        const localSelectedFilterCopy = JSON.parse(
            JSON.stringify(localSelectedFilters)
        );
        delete localSelectedFilterCopy[filterType];

        const selectedFilterConfig = filterTypeConfig[filterType];
        const childFilters = selectedFilterConfig?.childFilters ?? [];
        childFilters.forEach((filter) => {
            delete localSelectedFilterCopy[filter];
        });

        setLocalSelectedFilters(localSelectedFilterCopy);
    };

    const _getSize = (menuItem: TFilterTypeConfig<T>): number => {
        const item = localSelectedFilters[menuItem.key];
        if (Array.isArray(item)) {
            return item.length;
        }
        if (item && typeof item === "object") {
            return 1;
        }
        return 0;
    };

    // private functions end -----

    // hooks start -----
    useEffect(() => {
        if (!isOpen) return;

        if (!defaultFilterType) {
            _handleFilterTypeClick(filterItemsArr[0].key);
        } else if (defaultFilterType) {
            _handleFilterTypeClick(defaultFilterType);
        }
    }, [isOpen]);
    // hooks end -----

    // child components start -----
    const isDisabled = useMemo(
        () => Object.values(errorMessage).some((error) => !!error),
        [errorMessage]
    );
    const modalFooter = (
        <StyledModalFooter tokens={tokens}>
            <Button
                data-testid={FILTER_MODAL_DATA_IDS.CLEAR_ALL_BUTTON}
                type="secondary"
                onClick={_handleClearAllClick}
            >
                {secondaryCtaText}
            </Button>
            <Button
                data-testid={FILTER_MODAL_DATA_IDS.APPLY_BUTTON}
                type="primary"
                disabled={isDisabled}
                onClick={_handleApplyClick}
            >
                {primaryCtaText}
            </Button>
        </StyledModalFooter>
    );

    const modalTitle = (
        <StyledModalTitle
            data-testid={FILTER_MODAL_DATA_IDS.TITLE}
            tokens={tokens}
        >
            <span>{title}</span>
            <Button
                style={{ width: "24px", height: "24px" }}
                className={modalCloseButtonClass}
                onClick={_handleCloseClick}
                type="tertiary"
                data-testid={FILTER_MODAL_DATA_IDS.CROSS_BUTTON}
                icon={<CloseOutlined />}
            />
        </StyledModalTitle>
    );

    const _getErrorMessage = (selectedFilterConfig) => {
        const isEmptyFilter = Array.isArray(
            selectedFilterConfig?.filterOptions
        );
        return isEmptyFilter ? FILTER_MESSAGES.EMPTY : FILTER_MESSAGES.INVALID;
    };

    const _getFilterTypeProps = (
        selectedFilterConfig,
        localSelectedFilters
    ) => {
        return {
            isSingleFilterMode: mode === "single",
            selectedFilterType: selectedFilterType,
            selectedFilters: localSelectedFilters,
            setSelectedFilters: setLocalSelectedFilters,
            mode: selectedFilterConfig.mode,
            errorMessage: errorMessage,
            filterOptions: selectedFilterConfig.filterOptions ?? [],
            showSelectAll: selectedFilterConfig.showSelectAll,
            showTimePicker: selectedFilterConfig.showTimePicker,
            disabledQuickFilters:
                selectedFilterConfig?.disabledQuickFilters || [],
            minDate:
                selectedFilterConfig?.minDate ??
                dayjs().subtract(24, "month").startOf("day").valueOf(),
            childFilters: selectedFilterConfig?.childFilters ?? [],
            maxDate:
                selectedFilterConfig?.maxDate ?? dayjs().endOf("day").valueOf(),
            handleError: handleError,
            isRequired: selectedFilterConfig?.isRequired ?? false,
            maxAllowedRange:
                selectedFilterConfig?.maxAllowedRange ?? MAX_ALLOWED_DATE_RANGE,
            placeholder:
                selectedFilterConfig?.placeholder ??
                `Search By ${String(selectedFilterConfig?.title)}`,
            helperText: selectedFilterConfig?.helperText ?? "",
        };
    };
    const _renderFilterRHS = () => {
        const selectedFilterConfig = filterTypeConfig[selectedFilterType];

        if (isLoading) {
            return (
                <StyledLoaderWrapper
                    data-testid={FILTER_MODAL_DATA_IDS.LOADING_WRAPPER}
                    tokens={tokens}
                >
                    <Loader size="medium" />
                    <div>Loading filters, please wait...</div>
                </StyledLoaderWrapper>
            );
        }

        if (
            !["custom", "dateRange"].includes(selectedFilterConfig?.type) &&
            isEmpty(selectedFilterConfig?.filterOptions) &&
            !isLoading
        ) {
            const { title, description, icon, showRefreshText } =
                _getErrorMessage(selectedFilterConfig);
            return (
                <StyledNoResultWrapper
                    data-testid={FILTER_MODAL_DATA_IDS.NO_RESULT_WRAPPER}
                    tokens={tokens}
                >
                    {icon}
                    <div>{title}</div>
                    <div>{description}</div>
                    {showRefreshText ? (
                        <div>Please try again later.</div>
                    ) : null}
                </StyledNoResultWrapper>
            );
        }

        const childProps = _getFilterTypeProps(
            selectedFilterConfig,
            localSelectedFilters
        );
        switch (selectedFilterConfig.type) {
            case FILTER_TYPES.checkbox:
                return <CheckboxFilters<T> {...childProps} />;
            case FILTER_TYPES.radio:
                return <RadioFilters<T> {...childProps} />;
            case FILTER_TYPES.list:
                return <ListFilters<T> {...childProps} />;
            case FILTER_TYPES.dateRange:
                return <DateRangeFilter<T> {...childProps} />;
            case FILTER_TYPES.custom:
                return (
                    <StyledCustomFilterRenderer
                        data-testid={
                            FILTER_MODAL_DATA_IDS.CUSTOM_RENDERER_WRAPPER
                        }
                    >
                        {selectedFilterConfig.customFilterRenderer ? (
                            <selectedFilterConfig.customFilterRenderer
                                {...childProps}
                            />
                        ) : null}
                    </StyledCustomFilterRenderer>
                );
        }
    };
    // child components end -----

    const renderMenuItemStatus = (
        isError: boolean,
        menuItem: TFilterTypeConfig<T>
    ) => {
        const renderErrorStatus = (
            <AlertCircleOutlined
                className={errorIcon}
                color={tokens.color.icon.iconDanger}
            />
        );

        if (isError) {
            return renderErrorStatus;
        }
        const renderFilterCount = (
            <StyledFilterCount
                tokens={tokens}
                onClick={(event) => {
                    event.stopPropagation();
                    _handleClearFilter(menuItem.key);
                }}
            >
                {_getSize(menuItem)}
                <span className={removeClass}>
                    <XOutlined size="small" />
                </span>
            </StyledFilterCount>
        );
        if (_getSize(menuItem)) {
            return renderFilterCount;
        }

        return <FilterCountPlaceHolder />;
    };

    const _renderFooter = () => {
        if (isLoading) return null;
        const { helperText = "" } = filterTypeConfig[selectedFilterType];
        const errorText = errorMessage[selectedFilterType];
        if (errorText) {
            return (
                <HelperText
                    className={getFooterClassName(tokens)}
                    type={TypographyTypes.danger}
                >
                    {errorText}
                </HelperText>
            );
        }
        if (helperText) {
            return (
                <HelperText className={getFooterClassName(tokens)}>
                    Note: {helperText}
                </HelperText>
            );
        }
        return null;
    };
    return (
        <StyledFilterModal
            data-testid={FILTER_MODAL_DATA_IDS.MODAL}
            maskClosable
            destroyOnClose
            open={isOpen}
            classNames={{
                footer: filterModalFooterClass,
                body: filterModalBodyClass,
            }}
            closable={false}
            footer={modalFooter}
            title={modalTitle}
            onCancel={_handleCloseClick}
        >
            <StyledFilterMenuWrapper
                data-testid={FILTER_MODAL_DATA_IDS.FILTER_TYPES}
                tokens={tokens}
            >
                {filterItemsArr.map((menuItem) => {
                    const _disabledState =
                        filterTypeConfig[menuItem.key]?.isDisabled?.(
                            localSelectedFilters
                        );

                    const activeItemDataId =
                        menuItem.key === selectedFilterType
                            ? {
                                  "data-testid":
                                      FILTER_MODAL_DATA_IDS.ACTIVE_FILTER_TYPE,
                              }
                            : {};
                    const isError =
                        (errorMessage?.[menuItem.key]?.length ?? 0) > 0;
                    return (
                        <Tooltip
                            autoAdjustOverflow={false}
                            placement="right"
                            key={menuItem.key as string}
                            title={_disabledState?.reason}
                        >
                            <StyledFilterMenuItem
                                {...activeItemDataId}
                                tokens={tokens}
                                active={menuItem.key === selectedFilterType}
                                disabled={_disabledState?.value}
                                onClick={() =>
                                    _handleFilterTypeClick(menuItem.key)
                                }
                            >
                                <span
                                    className={
                                        isError ? errorTitle(tokens) : ""
                                    }
                                    data-testid={
                                        FILTER_MODAL_DATA_IDS.FILTER_TYPE_TITLE
                                    }
                                >
                                    {menuItem.title}
                                </span>
                                {renderMenuItemStatus(isError, menuItem)}
                            </StyledFilterMenuItem>
                        </Tooltip>
                    );
                })}
            </StyledFilterMenuWrapper>
            <FilterRendererWrapper>
                {_renderFilterRHS()}
                {_renderFooter()}
            </FilterRendererWrapper>
        </StyledFilterModal>
    );
};
